import api from './api';

const TABLE = 'MARKET_Store';
const PUBLIC_VIEW = 'vw_MARKET_StorePublic';
const API_BASE = process.env.REACT_APP_API_BASE || 'https://figazuresql.swancreate.com/api/crm';

// Strip the cashier PIN defensively so it never reaches UI/state, even when we
// fall back to the base table (before the public view has been scaffolded).
const stripPin = (s) => {
  if (s && typeof s === 'object' && 'CashierPin' in s) {
    const { CashierPin, ...rest } = s;
    return rest;
  }
  return s;
};

// Once per session: prefer the PIN-free public view; if it isn't scaffolded yet,
// fall back to the base table (and strip the PIN) so the storefront never uses it.
let viewAvailable = true;
const publicList = async () => {
  if (viewAvailable) {
    try {
      return await api.get(PUBLIC_VIEW);
    } catch {
      viewAvailable = false;
    }
  }
  const rows = await api.get(TABLE);
  return Array.isArray(rows) ? rows.map(stripPin) : rows;
};

export const storeService = {
  // Admin (auth-gated) reads/writes use the base table — it includes CashierPin.
  getAll: () => api.get(TABLE),
  getById: (id) => api.getById(TABLE, id),
  create: (data) => api.post(TABLE, data),
  update: (id, data) => api.patch(TABLE, id, data),
  delete: (id) => api.delete(TABLE, id),

  // Public/storefront reads use the PIN-free view (with table fallback).
  getBySlug: async (slug) => {
    const stores = await publicList();
    return stores.find(s => s.Slug === slug && s.isActive) || null;
  },

  getByOwner: async (userId) => {
    const stores = await publicList();
    return stores.filter(s => s.OwnerId === userId);
  },

  getActive: async () => {
    const stores = await publicList();
    return stores.filter(s => s.isActive);
  },

  /**
   * Verify a cashier PIN for a store. Returns the store apiid on match, else null.
   * Filters server-side by (Slug, CashierPin) so only the matching store is ever
   * returned (no other store's PIN reaches the device), then re-checks the echoed
   * value as defense-in-depth. The entered PIN travels over HTTPS but is never
   * exposed on shopper-facing pages.
   */
  verifyCashierPin: async (slug, pin) => {
    const code = String(pin || '').trim();
    if (!slug || !code) return null;
    try {
      const url = `${API_BASE}/${TABLE}?Slug=${encodeURIComponent(slug)}&CashierPin=${encodeURIComponent(code)}&isActive=true`;
      const rows = await api.getUrl(url);
      if (Array.isArray(rows) && rows.length) {
        const store = rows[0];
        // Defense-in-depth: only trust the match if the returned PIN equals what was entered.
        if (store.CashierPin == null || String(store.CashierPin) === code) {
          return store.apiid;
        }
      }
    } catch (err) {
      console.error('[storeService] verifyCashierPin failed:', err);
    }
    return null;
  },
};

export default storeService;
